import React, { ReactElement } from "react";
//#region types
import { DialogContentHandles, DialogProps } from "models/propTypes/dialogPropTypes";
import { TranslationKeys } from "translations";
import cssExports from "theme/styles/main.module.scss";
import { FileConstants, HTMLConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
import { FormHandles, GenericFormSection, WizardDialogProps } from "models/propTypes/genericFormProps";
//#endregion
//#region components
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Portal } from "@mui/material";
import useFloatingMessages from "../messages/useFloatingMessages";
import useMountedState from "../useMountedState";
import useWizardDialogContext from "../wizardDialog/context/useWizardDialogContext";
import useFilePreviewOnDialog from "../fileViewer/usePreviewOnDialog";
import FormValidity from "../forms/formValidity";
import * as Transitions from "../transitions";
import Icon from "../icon";
import StyledButton from "../buttons/styledButton";
import CloseIcon from "@mui/icons-material/Close";
//#endregion
import clsx from "clsx";
import { LocalizationHelperInstance } from "utils/helpers";

const WizardDialog: React.FC<DialogProps> = (props) => {
  const onClose = () => {
    props.onClose && props.onClose();
    props.closeDialog && props.closeDialog();
  };

  const [loading, setLoading] = React.useState<boolean>(false);
  const { enqueueMessage } = useFloatingMessages();
  const { getMounted } = useMountedState();

  const wizardContext = useWizardDialogContext();
  const innerRef = React.useRef<FormHandles & DialogContentHandles>(null);

  const mount = document.getElementById(HTMLConstants.LayoutPortalId);

  const { assignPreviewRef, assignRef, containerDimensions } = useFilePreviewOnDialog(
    props.previewElementPlacement === "left"
  );

  const childElement = React.Children.only(props.children as ReactElement);

  const onClickPrevious = () => {
    if (wizardContext?.currentStep) {
      wizardContext?.previous();
    }
  };

  const wizardFields: GenericFormSection<any>[] = React.useMemo(
    () => wizardContext?.wizardFields,
    [wizardContext?.wizardFields]
  );

  const currentWizardField: GenericFormSection<any> = React.useMemo(
    () => wizardFields[wizardContext?.currentStep ?? 0],
    [wizardContext?.currentStep, wizardFields]
  );

  const values = React.useMemo(() => {
    return wizardContext?.values;
  }, [wizardContext?.values]);

  const currentWizardHasErrors = React.useMemo(() => {
    return wizardContext?.currentStepErrors;
  }, [wizardContext?.currentStepErrors]);

  const currentWizardSummaryValue = React.useMemo(() => {
    const extraProps = currentWizardField?.extraProps as WizardDialogProps;
    return extraProps?.leftSummaryValue && extraProps.leftSummaryValue(values);
  }, [values, wizardContext?.currentStep]);

  const onClickNext = () => {
    const currentStep = wizardContext?.currentStep ?? 0;
    const numberOfSteps = wizardContext?.numOfSteps;
    if (numberOfSteps) {
      if (currentStep < numberOfSteps - 1) {
        wizardContext?.next();
      }
      if (numberOfSteps - 1 === currentStep) {
        actionClick();
      }
    }
  };

  const actionClick = (onClickFunc?: () => Promise<void>, successMessage?: string, persistDialogOnSubmit?: boolean) => {
    setLoading(true);
    (onClickFunc
      ? onClickFunc()
      : innerRef.current
      ? innerRef.current.submit()
      : new Promise<void>((resolve) => {
          resolve();
        })
    )
      .then((res) => {
        successMessage &&
          enqueueMessage(
            LocalizationHelperInstance.getFormattedMessage(successMessage),
            StyleEnums.DisplayType.Success
          );
        props.onSubmit && props.onSubmit(res);
        if (!persistDialogOnSubmit) props.closeDialog && props.closeDialog();

        getMounted() && setLoading(false);
      })
      .catch((error) => {
        props.onError && props.onError(error, props.closeDialog);
        getMounted() && setLoading(false);
      });
  };

  return (
    <>
      <Portal container={mount}>{props.overlayAnimationComponent}</Portal>
      <FormValidity
        render={(isValid, onValidityChange) => (
          <Dialog
            ref={assignRef}
            aria-labelledby="dialog-title"
            open={props.isOpen}
            maxWidth="md"
            fullWidth={props.fullWidth}
            hideBackdrop={false}
            className={clsx(
              props.className,
              cssExports.dialogFullHeight,
              props.type ? (props.type === StyleEnums.DisplayType.Danger ? cssExports.danger : cssExports.warning) : ""
            )}
            classes={{
              root: cssExports.dialogRoot,
              paperScrollPaper: clsx(cssExports.dialogScrollPaper, cssExports.sm),
              paperWidthMd: cssExports.dialogMaxMd,
              paperWidthSm: cssExports.dialogMaxSm,
            }}
            TransitionComponent={Transitions.DialogTransition}
            onClose={onClose}
            closeAfterTransition
          >
            <Grid
              ref={assignPreviewRef}
              id={FileConstants.DialogFilePreviewPortalId}
              className={cssExports.filePreviewOverlay}
              style={{
                width: containerDimensions ? `${containerDimensions}px` : undefined,
              }}
            ></Grid>
            <IconButton aria-label="close" className={cssExports.closeButton} onClick={onClose}>
              <CloseIcon />
            </IconButton>
            <DialogTitle id="dialog-title" className={cssExports.dialogHeader}>
              <Grid container className={cssExports.dialogTitleOuter} alignItems="center">
                <Grid item>
                  {currentWizardField?.icon ? (
                    <span className={cssExports.icon}>
                      <Icon icon={currentWizardField?.icon} />
                    </span>
                  ) : (
                    ""
                  )}
                </Grid>
                <Grid item xs>
                  <Grid
                    item
                    className={
                      currentWizardField?.descriptionText ? cssExports.dialogTitleWithSubTitle : cssExports.dialogTitle
                    }
                  >
                    {`${(wizardContext?.currentStep ?? 0) + 1}. ${LocalizationHelperInstance.getFormattedMessage(
                      currentWizardField?.label ?? ""
                    )}`}
                  </Grid>
                  <Grid item className={cssExports.dialogSubTitle}>
                    {typeof currentWizardField?.descriptionText === "string" &&
                      LocalizationHelperInstance.getFormattedMessage(currentWizardField?.descriptionText ?? "")}
                  </Grid>
                </Grid>
              </Grid>
            </DialogTitle>

            <DialogContent className={cssExports.dialogContent}>
              {React.cloneElement(childElement, {
                ref: innerRef,
                onFormValidationChange: onValidityChange,
              })}
            </DialogContent>
            <DialogActions className={cssExports.dialogActions}>
              <Grid container direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                <Grid item className={cssExports.dialgoSummary}>
                  <Grid container spacing={1}>
                    <Grid item className={cssExports.titleText}>
                      {(currentWizardField?.extraProps as WizardDialogProps)?.leftSummaryText}
                    </Grid>
                    <Grid item>{currentWizardSummaryValue}</Grid>
                  </Grid>
                </Grid>
                <Grid item className={cssExports.dialogButtons}>
                  {!wizardContext?.currentStep && (
                    <StyledButton autoFocus onClick={onClose} secondary size="sm">
                      {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Cancel)}
                    </StyledButton>
                  )}
                  {!!wizardContext?.currentStep && (
                    <StyledButton autoFocus onClick={onClickPrevious} secondary size="sm">
                      {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Previous)}
                    </StyledButton>
                  )}
                  <StyledButton
                    size="sm"
                    key={"action-next"}
                    displayType={StyleEnums.DisplayType.Info}
                    onClick={onClickNext}
                    disabled={!!currentWizardHasErrors}
                    isSubmitting={loading}
                  >
                    {LocalizationHelperInstance.getFormattedMessage(
                      (wizardContext?.currentStep ?? 0) + 1 === wizardContext?.numOfSteps
                        ? TranslationKeys.Common.Save
                        : TranslationKeys.Common.Next
                    )}
                  </StyledButton>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>
        )}
      />
    </>
  );
};

export default WizardDialog;
